import { isEmpty, isNil } from 'lodash';
import { useContext } from 'react';
import { Menu } from '@shared/components/Menu';
import { Switch } from '@shared/components/Switch';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { MBIconV2 } from '@shared/components/image';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { ColorThemeContext } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { ShareQRCodeSection } from './ShareQRCodeSection';

interface Props {
  privateShare?: {
    url: string;
    onCopyQr(data: string): void;
    onCopyLink(uri: string): void;
  };
  canCreatePublicCode: boolean;
  hasPublicQrCode: boolean;
  publicUrl: string | undefined;
  onSwitchPublic(isPublic: boolean): void;
  onCopyPublicQr(data: string): void;
  onCopyPublicLink(uri: string): void;
}

export function ShareQRCode({
  privateShare,
  hasPublicQrCode,
  canCreatePublicCode,
  publicUrl,
  onCopyPublicQr,
  onCopyPublicLink,
  onSwitchPublic,
}: Props) {
  const colors = useContext(ColorThemeContext);
  const hasPublicUrl = !isNil(publicUrl) && !isEmpty(publicUrl);
  const canPrivateShare = !isNil(privateShare);
  return (
    <>
      {canPrivateShare && (
        <>
          <Menu.Section title={i18n('qrCode.share.teamAccess')}>
            <ShareQRCodeSection
              url={privateShare.url}
              onCopyQr={privateShare.onCopyQr}
              onCopyLink={privateShare.onCopyLink}
            />
          </Menu.Section>
        </>
      )}
      <Menu.Separator />
      {(canCreatePublicCode || hasPublicQrCode) && (
        <Menu.Section title={i18n('qrCode.share.publicAccess')}>
          {canCreatePublicCode && (
            <MBTouchableOpacity onPress={() => onSwitchPublic(!hasPublicQrCode)}>
              <View centerVertically row gap={2}>
                <MBIconV2 name="eye-show" size={24} color={colors.foregroundActive} />
                <View paddingRight={2} flex>
                  <Text size="body1">{i18n('qrCode.share.createPublic')}</Text>
                </View>
                <Switch
                  label={i18n('qrCode.share.createPublic')}
                  isSelected={hasPublicQrCode}
                  onValueChange={onSwitchPublic}
                />
              </View>
            </MBTouchableOpacity>
          )}
          {hasPublicQrCode && !hasPublicUrl && (
            <View center paddingVertical={8}>
              <MBLoadingIndicator />
            </View>
          )}
          {hasPublicQrCode && hasPublicUrl && !isEmpty(publicUrl) && (
            <>
              <ShareQRCodeSection
                url={publicUrl}
                onCopyQr={onCopyPublicQr}
                onCopyLink={onCopyPublicLink}
              />
            </>
          )}
        </Menu.Section>
      )}
      {hasPublicQrCode && hasPublicUrl && !isEmpty(publicUrl) && (
        <Menu.Note>{i18n('qrCode.share.publicAccessDisclaimer')}</Menu.Note>
      )}
    </>
  );
}
